import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import getServerBaseUrl from '../../utils/serverBaseUrl';

const SERVER_BASE = getServerBaseUrl();

function buildMapillaryEmbedUrl(imageId, style = 'photo') {
  return `https://www.mapillary.com/embed?image_key=${encodeURIComponent(imageId)}&style=${encodeURIComponent(style || 'photo')}`;
}

function makePin(label, background) {
  return L.divIcon({
    className: '',
    html: `<div style="width:28px;height:28px;border-radius:50%;background:${background};border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;font-size:12px;font-weight:700;box-shadow:0 2px 6px rgba(0,0,0,.35);">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function LocationPicker({ onPick }) {
  useMapEvents({
    click(event) {
      onPick(event.latlng);
    },
  });
  return null;
}

function FocusMap({ lat, lng }) {
  const map = useMap();

  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.setView([lat, lng], Math.max(map.getZoom(), 14));
  }, [lat, lng, map]);

  return null;
}

function emptyDraft() {
  return {
    disabled: false,
    name: '',
    lat: 51.5072,
    lng: -0.1276,
    mapillaryImageId: '',
    mapillaryStyle: 'photo',
    mapillaryThumbUrl: '',
    mapillaryCapturedAt: '',
    mapillaryCompassAngle: '',
    sourceName: 'Mapillary',
    sourceUrl: '',
    attribution: 'Mapillary street-level imagery',
  };
}

function normalizeDraft(entry) {
  return {
    ...emptyDraft(),
    ...entry,
    disabled: !!entry?.disabled,
    name: entry?.name || '',
    mapillaryImageId: entry?.mapillaryImageId || '',
    mapillaryStyle: entry?.mapillaryStyle || 'photo',
    mapillaryThumbUrl: entry?.mapillaryThumbUrl || '',
    mapillaryCapturedAt: entry?.mapillaryCapturedAt || '',
    mapillaryCompassAngle: entry?.mapillaryCompassAngle ?? '',
    sourceName: entry?.sourceName || 'Mapillary',
    sourceUrl: entry?.sourceUrl || '',
    attribution: entry?.attribution || 'Mapillary street-level imagery',
    lat: Number.isFinite(Number(entry?.lat)) ? Number(entry.lat) : emptyDraft().lat,
    lng: Number.isFinite(Number(entry?.lng)) ? Number(entry.lng) : emptyDraft().lng,
  };
}

export default function WhereAmILocationManager({ enabled }) {
  const [locations, setLocations] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [searchResults, setSearchResults] = useState([]);
  const [placeSearch, setPlaceSearch] = useState({ query: '', results: [], searching: false, error: '' });
  const [status, setStatus] = useState({ loading: false, saving: false, message: '', error: '' });

  const loadLocations = useCallback(async () => {
    if (!enabled) return;
    setStatus((current) => ({ ...current, loading: true, error: '', message: '' }));
    try {
      const response = await fetch(`${SERVER_BASE}/api/whereami/locations`);
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'Failed to load locations');
      }
      setLocations(Array.isArray(payload.locations) ? payload.locations : []);
      setStatus((current) => ({ ...current, loading: false }));
    } catch (error) {
      setStatus((current) => ({ ...current, loading: false, error: String(error.message || error) }));
    }
  }, [enabled]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const applySearchSelection = useCallback((result, nextLat, nextLng) => {
    if (!result) return;
    setDraft((current) => ({
      ...current,
      lat: Number.isFinite(nextLat) ? nextLat : current.lat,
      lng: Number.isFinite(nextLng) ? nextLng : current.lng,
      mapillaryImageId: result.id,
      mapillaryThumbUrl: result.thumbUrl || '',
      mapillaryCapturedAt: result.capturedAt || '',
      mapillaryCompassAngle: result.compassAngle ?? '',
      sourceName: 'Mapillary',
      sourceUrl: `https://www.mapillary.com/app/?image_key=${encodeURIComponent(result.id)}`,
      attribution: current.attribution || 'Mapillary street-level imagery',
    }));
  }, []);

  const searchMapillary = useCallback(async (lat, lng) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    setStatus((current) => ({ ...current, loading: true, error: '', message: '' }));
    try {
      const response = await fetch(`${SERVER_BASE}/api/whereami/mapillary/search?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}&limit=6`);
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'Mapillary search failed');
      }
      const results = Array.isArray(payload.images) ? payload.images : [];
      setSearchResults(results);
      if (results[0]) {
        applySearchSelection(results[0], lat, lng);
        setStatus((current) => ({ ...current, loading: false, message: `Loaded ${results.length} nearby street view result${results.length === 1 ? '' : 's'}.` }));
      } else {
        setStatus((current) => ({ ...current, loading: false, message: 'No nearby Mapillary street view was found for that point.' }));
      }
    } catch (error) {
      setSearchResults([]);
      setStatus((current) => ({ ...current, loading: false, error: String(error.message || error) }));
    }
  }, [applySearchSelection]);

  const handleMapPick = useCallback((latlng) => {
    const nextLat = Number(latlng.lat.toFixed(6));
    const nextLng = Number(latlng.lng.toFixed(6));
    setDraft((current) => ({ ...current, lat: nextLat, lng: nextLng }));
    searchMapillary(nextLat, nextLng);
  }, [searchMapillary]);

  const searchPlaces = useCallback(async () => {
    const query = placeSearch.query.trim();
    if (!query) {
      setPlaceSearch((current) => ({ ...current, results: [], error: 'Enter a place to search.' }));
      return;
    }

    setPlaceSearch((current) => ({ ...current, searching: true, error: '', results: [] }));
    try {
      const response = await fetch(`${SERVER_BASE}/api/whereami/geocode?q=${encodeURIComponent(query)}&limit=6`);
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'Place search failed');
      }

      const results = Array.isArray(payload.results) ? payload.results : [];
      setPlaceSearch((current) => ({
        ...current,
        searching: false,
        results,
        error: results.length ? '' : 'No places matched that search.',
      }));
    } catch (error) {
      setPlaceSearch((current) => ({ ...current, searching: false, results: [], error: String(error.message || error) }));
    }
  }, [placeSearch.query]);

  const applyPlaceSelection = useCallback((result) => {
    if (!result) return;

    const nextLat = Number(result.lat.toFixed(6));
    const nextLng = Number(result.lng.toFixed(6));

    setDraft((current) => ({
      ...current,
      lat: nextLat,
      lng: nextLng,
      name: current.name || result.name,
    }));
    setPlaceSearch((current) => ({ ...current, query: result.name, results: [] }));
    searchMapillary(nextLat, nextLng);
  }, [searchMapillary]);

  const handleSave = useCallback(async () => {
    setStatus((current) => ({ ...current, saving: true, error: '', message: '' }));
    try {
      const response = await fetch(`${SERVER_BASE}/api/whereami/locations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entry: draft, index: selectedIndex }),
      });
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'Failed to save location');
      }
      setLocations(Array.isArray(payload.locations) ? payload.locations : []);
      setSelectedIndex(payload.index);
      setDraft(normalizeDraft(payload.entry));
      setStatus((current) => ({ ...current, saving: false, message: 'Location saved to locations.json.' }));
    } catch (error) {
      setStatus((current) => ({ ...current, saving: false, error: String(error.message || error) }));
    }
  }, [draft, selectedIndex]);

  const previewUrl = useMemo(() => {
    if (!draft.mapillaryImageId) return null;
    return buildMapillaryEmbedUrl(draft.mapillaryImageId, draft.mapillaryStyle || 'photo');
  }, [draft.mapillaryImageId, draft.mapillaryStyle]);

  if (!enabled) return null;

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow dark:border-slate-700 dark:bg-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Where Am I Location Builder</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Click the map to find nearby Mapillary street view, preview it, then save straight into locations.json.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setSelectedIndex(null);
              setDraft(emptyDraft());
              setSearchResults([]);
              setPlaceSearch({ query: '', results: [], searching: false, error: '' });
              setStatus((current) => ({ ...current, message: '', error: '' }));
            }}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 dark:border-slate-600 dark:text-slate-200"
          >
            New Entry
          </button>
          <button
            type="button"
            onClick={loadLocations}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 dark:border-slate-600 dark:text-slate-200"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Existing Locations
            <select
              value={selectedIndex ?? ''}
              onChange={(event) => {
                const nextValue = event.target.value;
                if (nextValue === '') {
                  setSelectedIndex(null);
                  setDraft(emptyDraft());
                  setSearchResults([]);
                  setPlaceSearch({ query: '', results: [], searching: false, error: '' });
                  return;
                }
                const index = Number(nextValue);
                setSelectedIndex(index);
                setDraft(normalizeDraft(locations[index] || emptyDraft()));
                setSearchResults([]);
                setPlaceSearch({ query: '', results: [], searching: false, error: '' });
              }}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            >
              <option value="">Create a new location</option>
              {locations.map((location, index) => (
                <option key={`${location.name || 'location'}-${index}`} value={index}>
                  {location.name || `Location ${index + 1}`}
                </option>
              ))}
            </select>
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 sm:col-span-2">
              Name
              <input
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Latitude
              <input
                type="number"
                step="0.000001"
                value={draft.lat}
                onChange={(event) => setDraft((current) => ({ ...current, lat: Number(event.target.value) }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Longitude
              <input
                type="number"
                step="0.000001"
                value={draft.lng}
                onChange={(event) => setDraft((current) => ({ ...current, lng: Number(event.target.value) }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Mapillary Image ID
              <input
                value={draft.mapillaryImageId}
                onChange={(event) => setDraft((current) => ({ ...current, mapillaryImageId: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              Source URL
              <input
                value={draft.sourceUrl}
                onChange={(event) => setDraft((current) => ({ ...current, sourceUrl: event.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
            </label>
          </div>

          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={draft.disabled}
              onChange={(event) => setDraft((current) => ({ ...current, disabled: event.target.checked }))}
            />
            Disabled
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => searchMapillary(draft.lat, draft.lng)}
              className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700"
            >
              Find Street View
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!draft.name || !draft.mapillaryImageId || status.saving}
              className={`rounded-lg px-3 py-2 text-sm font-semibold text-white ${!draft.name || !draft.mapillaryImageId || status.saving ? 'bg-slate-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}
            >
              {status.saving ? 'Saving...' : 'Save Location'}
            </button>
          </div>

          {status.error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">{status.error}</div>}
          {status.message && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200">{status.message}</div>}
          {status.loading && <div className="text-sm text-slate-500 dark:text-slate-400">Loading...</div>}

          {searchResults.length > 0 && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-slate-700 dark:text-slate-200">Nearby Street View Candidates</div>
              <div className="grid gap-2">
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    onClick={() => applySearchSelection(result, draft.lat, draft.lng)}
                    className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left text-sm ${draft.mapillaryImageId === result.id ? 'border-sky-500 bg-sky-50 dark:bg-sky-950' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800'}`}
                  >
                    <span className="font-medium text-slate-800 dark:text-slate-100">{result.id}</span>
                    <span className="text-slate-500 dark:text-slate-400">{result.distanceMeters} m away</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
            <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">Find a place</div>
            <form
              className="mt-2 flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                searchPlaces();
              }}
            >
              <input
                value={placeSearch.query}
                onChange={(event) => setPlaceSearch((current) => ({ ...current, query: event.target.value, error: '' }))}
                placeholder="Search for a town, landmark, or address"
                className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
              <button
                type="submit"
                disabled={placeSearch.searching}
                className={`rounded-lg px-3 py-2 text-sm font-semibold text-white ${placeSearch.searching ? 'bg-slate-400' : 'bg-sky-600 hover:bg-sky-700'}`}
              >
                {placeSearch.searching ? 'Searching...' : 'Search'}
              </button>
            </form>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Search first, then click the map to fine-tune the exact point before pulling nearby street view.</p>
            {placeSearch.error && <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">{placeSearch.error}</div>}
            {placeSearch.results.length > 0 && (
              <div className="mt-2 grid gap-2">
                {placeSearch.results.map((result) => (
                  <button
                    key={`${result.name}-${result.lat}-${result.lng}`}
                    type="button"
                    onClick={() => applyPlaceSelection(result)}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-800 hover:border-sky-300 hover:bg-sky-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-800"
                  >
                    {result.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 shadow-sm dark:border-slate-700" style={{ height: 260 }}>
            <MapContainer center={[draft.lat, draft.lng]} zoom={13} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />
              <FocusMap lat={draft.lat} lng={draft.lng} />
              <LocationPicker onPick={handleMapPick} />
              <Marker position={[draft.lat, draft.lng]} icon={makePin('P', '#0284c7')} />
            </MapContainer>
          </div>

          {previewUrl ? (
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-950 shadow-sm dark:border-slate-700" style={{ height: 260 }}>
              <iframe
                title="Mapillary preview"
                src={previewUrl}
                className="h-full w-full border-0"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="flex h-[260px] items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
              Pick a point or enter a Mapillary image ID to preview street view.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}